import { createContext, useContext, useMemo, type PropsWithChildren } from "react";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import { DEFAULT_TRACKING_OPTIONS } from "../options";
import type { TrackingStrategies } from "../contracts";
import type { TrackingInspectorSnapshot, TrackingInspectorSummary } from "./status";

export interface InspectorSettings {
  interpolationEnabled: boolean;
  strategies: TrackingStrategies;
  nanoMode: "auto" | "always";
  recoveryEnabled?: boolean;
}

export const DEFAULT_INSPECTOR_SETTINGS: InspectorSettings = {
  interpolationEnabled: true,
  strategies: { ...DEFAULT_TRACKING_OPTIONS.strategies },
  nanoMode: "auto",
  recoveryEnabled: true,
};

export function sameInspectorSettings(a: InspectorSettings, b: InspectorSettings): boolean {
  return sameLocalSettings(a, b) && !!a.recoveryEnabled === !!b.recoveryEnabled;
}

function sameLocalSettings(a: InspectorSettings, b: InspectorSettings): boolean {
  return (
    a.interpolationEnabled === b.interpolationEnabled &&
    a.nanoMode === b.nanoMode &&
    a.strategies.sparse === b.strategies.sparse &&
    a.strategies.nano === b.strategies.nano &&
    a.strategies.hybrid === b.strategies.hybrid
  );
}

const copySettings = (settings: InspectorSettings): InspectorSettings => ({
  ...settings,
  strategies: { ...settings.strategies },
});

export interface TrackingInspectorState {
  enabled: boolean;
  open: boolean;
  tab: "inspect" | "run" | "strategies";
  filter: "all" | "problems";
  selectedTrackId: string | null;
  showSources: boolean;
  activeSettings: InspectorSettings;
  draftSettings: InspectorSettings;
  generation: number;
  summary: TrackingInspectorSummary | null;
  snapshot: TrackingInspectorSnapshot | null;
  publishDiagnostics: (diagnostics: {
    summary: TrackingInspectorSummary;
    snapshot: TrackingInspectorSnapshot | null;
  }) => void;
  setOpen: (open: boolean) => void;
  setTab: (tab: TrackingInspectorState["tab"]) => void;
  setFilter: (filter: TrackingInspectorState["filter"]) => void;
  selectTrack: (id: string) => void;
  setShowSources: (show: boolean) => void;
  updateDraft: (patch: Partial<InspectorSettings>) => void;
  apply: () => void;
  discard: () => void;
  resetDraft: () => void;
  rerun: () => void;
}

/** A separate store per player: frame diagnostics never rerender the entire player. */
export function createTrackingInspectorStore(enabled = false) {
  return createStore<TrackingInspectorState>()((set, get) => ({
    enabled,
    open: false,
    tab: "inspect",
    filter: "all",
    selectedTrackId: null,
    showSources: false,
    activeSettings: copySettings(DEFAULT_INSPECTOR_SETTINGS),
    draftSettings: copySettings(DEFAULT_INSPECTOR_SETTINGS),
    generation: 0,
    summary: null,
    snapshot: null,
    publishDiagnostics: ({ summary, snapshot }) => {
      if (!enabled) return;
      set((state) => {
        const visibleSnapshot = state.open ? snapshot : null;
        return {
          summary,
          snapshot: visibleSnapshot,
          selectedTrackId:
            visibleSnapshot && !visibleSnapshot.rows.some((row) => row.trackId === state.selectedTrackId)
              ? (visibleSnapshot.rows.find((row) => row.intervalId)?.trackId ??
                visibleSnapshot.rows[0]?.trackId ??
                null)
              : state.selectedTrackId,
        };
      });
    },
    setOpen: (open) => {
      if (enabled) set({ open, ...(!open && { snapshot: null }) });
    },
    setTab: (tab) => {
      if (enabled) set({ tab });
    },
    setFilter: (filter) => {
      if (enabled) set({ filter });
    },
    selectTrack: (selectedTrackId) => {
      if (enabled) set({ selectedTrackId });
    },
    setShowSources: (showSources) => {
      if (enabled) set({ showSources });
    },
    updateDraft: (patch) => {
      if (enabled) set((state) => ({ draftSettings: copySettings({ ...state.draftSettings, ...patch }) }));
    },
    apply: () => {
      const state = get();
      if (!enabled || sameInspectorSettings(state.activeSettings, state.draftSettings)) return;
      set({
        activeSettings: copySettings(state.draftSettings),
        generation: state.generation + (sameLocalSettings(state.activeSettings, state.draftSettings) ? 0 : 1),
        snapshot: null,
        summary: null,
      });
    },
    discard: () => {
      if (enabled) set({ draftSettings: copySettings(get().activeSettings) });
    },
    resetDraft: () => {
      if (enabled) set({ draftSettings: copySettings(DEFAULT_INSPECTOR_SETTINGS) });
    },
    rerun: () => {
      if (enabled) set((state) => ({ generation: state.generation + 1, snapshot: null, summary: null }));
    },
  }));
}

type InspectorStore = ReturnType<typeof createTrackingInspectorStore>;
const inactiveStore = createTrackingInspectorStore();
const InspectorContext = createContext<InspectorStore>(inactiveStore);

export function TrackingInspectorProvider({ enabled, children }: PropsWithChildren<{ enabled: boolean }>) {
  // Switching off the gate synchronously discards all overrides, diagnostics,
  // and appearance state without remounting the video element or timeline.
  const store = useMemo(() => createTrackingInspectorStore(enabled), [enabled]);
  return <InspectorContext.Provider value={store}>{children}</InspectorContext.Provider>;
}

export function useTrackingInspectorApi(): InspectorStore {
  return useContext(InspectorContext);
}

export function useTrackingInspectorStore<T>(selector: (state: TrackingInspectorState) => T): T {
  return useStore(useTrackingInspectorApi(), selector);
}
